// LoginSecurity.jsx
"use client";

import { useEffect, useState, useCallback } from "react";
import { useAlert } from "context/AlertContext";
import LocalDateTime from "services/date-converter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import Skeleton from "components/ui/Skeleton";
import apiLoginSecurity from "./api";

import {
  FaShieldAlt,
  FaKey,
  FaLock,
  FaMobileAlt,
  FaLaptop,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaHistory,
  FaGlobe,
  FaBell,
  FaUserShield,
  FaSignOutAlt,
  FaInfoCircle,
  FaEnvelope,
  FaDesktop,
  FaFingerprint,
  FaSyncAlt,
  FaRedo,
} from "react-icons/fa";

// ── Helpers ──
const scoreTone = (score) => {
  if (score >= 80) return { text: "text-success", bg: "bg-success",   soft: "bg-success_bg",  ring: "var(--color-success)"   };
  if (score >= 60) return { text: "text-warning", bg: "bg-warning",   soft: "bg-warning_bg",  ring: "var(--color-warning)"   };
  return             { text: "text-secondary",    bg: "bg-secondary", soft: "bg-secondary/10",ring: "var(--color-secondary)" };
};

const alertTone = (severity) => {
  switch (severity) {
    case "critical":
    case "danger":
      return { wrap: "bg-secondary/5 border-secondary",         title: "text-secondary", icon: FaExclamationTriangle, iconColor: "text-secondary" };
    case "warning":
      return { wrap: "bg-warning_bg border-warning_border",     title: "text-warning",   icon: FaExclamationTriangle, iconColor: "text-warning"   };
    default:
      return { wrap: "bg-info_bg border-info_border",           title: "text-info",      icon: FaInfoCircle,          iconColor: "text-info"      };
  }
};

const deviceIcon = (device = "") => {
  const d = device.toLowerCase();
  if (d.includes("iphone") || d.includes("android") || d.includes("mobile")) return FaMobileAlt;
  return FaLaptop;
};

// ── Small presentational pieces ──
const SectionCard = ({ icon: Icon, title, action, children, className = "" }) => (
  <div className={`bg-surface rounded-2xl shadow-sm border border-border p-5 ${className}`}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-semibold flex items-center gap-2 text-primary">
        <Icon size={14} /> {title}
      </h3>
      {action}
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, tone = "text-primary" }) => (
  <div className="bg-surface rounded-xl border border-border p-4 text-center">
    <p className={`text-2xl font-bold ${tone}`}>{value}</p>
    <p className="text-[11px] text-text_muted mt-1 leading-tight">{label}</p>
  </div>
);

const InfoRow = ({ icon: Icon, label, value, badge }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border/60 last:border-0">
    <div className="flex items-center gap-2.5 min-w-0">
      <Icon size={13} className="text-text_muted shrink-0" />
      <span className="text-xs text-text_secondary truncate">{label}</span>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {value && <span className="text-xs font-medium text-text_primary">{value}</span>}
      {badge}
    </div>
  </div>
);

const Pill = ({ ok, okText, badText, okClass = "bg-success_bg text-success border-success_border", badClass = "bg-secondary/10 text-secondary border-secondary/20" }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${ok ? okClass : badClass}`}>
    {ok ? <FaCheckCircle size={9} /> : <FaTimesCircle size={9} />}
    {ok ? okText : badText}
  </span>
);

export default function LoginSecurity() {
  const { showAlert } = useAlert();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const [confirmData, setConfirmData] = useState({ isOpen: false, resolve: null });

  const openConfirm = (options) =>
    new Promise((resolve) => setConfirmData({ ...options, isOpen: true, resolve }));

  const handleConfirmClose = (result) => {
    confirmData.resolve?.(result);
    setConfirmData((prev) => ({ ...prev, isOpen: false, resolve: null }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await apiLoginSecurity.getLoginSecurity();
      if (res?.data?.status) {
        setData(res.data.data);
      } else {
        setError(true);
        showAlert({ type: "warning", message: res?.data?.message || "Failed to load security data" });
      }
    } catch (err) {
      setError(true);
      showAlert({ type: "error", message: err?.response?.data?.message || "An unexpected error occurred." });
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRevokeSession = async (session) => {
    const confirmed = await openConfirm({
      title: "Revoke session",
      message: `Sign out the session on ${session.device || "this device"}? That device will need to log in again.`,
      confirmText: "Revoke",
      cancelText: "Cancel",
      icon: <FaSignOutAlt size={22} className="text-secondary" />,
    });
    if (!confirmed) return;

    setBusyId(session.id);
    try {
      const res = await apiLoginSecurity.revokeSession(session.id);
      if (res?.data?.status) {
        showAlert({ type: "success", message: res.data.message || "Session revoked" });
        fetchData();
      } else {
        showAlert({ type: "warning", message: res?.data?.message || "Could not revoke session" });
      }
    } catch (err) {
      showAlert({ type: "error", message: err?.response?.data?.message || "Failed to revoke session" });
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeOthers = async () => {
    const confirmed = await openConfirm({
      title: "Sign out other sessions",
      message: "This will sign you out of all other devices and browsers. Your current session stays active.",
      confirmText: "Sign out all",
      cancelText: "Cancel",
      icon: <FaSignOutAlt size={22} className="text-secondary" />,
    });
    if (!confirmed) return;

    setRevokingAll(true);
    try {
      const res = await apiLoginSecurity.revokeOtherSessions(data?.sessions?.current?.id);
      if (res?.data?.status) {
        showAlert({ type: "success", message: res.data.message || "Other sessions signed out" });
        fetchData();
      } else {
        showAlert({ type: "warning", message: res?.data?.message || "Could not sign out sessions" });
      }
    } catch (err) {
      showAlert({ type: "error", message: err?.response?.data?.message || "Failed to sign out sessions" });
    } finally {
      setRevokingAll(false);
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="h-full overflow-y-auto bg-background">
        <div className="max-w-[100rem] mx-auto w-full py-3">

          {/* Header (chrome stays static, only the copy shimmers) */}
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-primary">Login &amp; Security</h1>
              <Skeleton className="h-3.5 w-64 mt-2" />
            </div>
            <button
              disabled
              className="px-3 py-2 rounded-lg text-xs font-medium text-text_secondary bg-surface border border-border flex items-center gap-2 opacity-60"
            >
              <FaSyncAlt size={11} /> Refresh
            </button>
          </div>

          {/* Security Score Hero */}
          <div className="mb-5">
            <div className="bg-surface rounded-2xl shadow-sm border border-border p-5 md:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="flex items-center gap-5">
                  <Skeleton className="w-24 h-24 shrink-0" rounded="rounded-full" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-lg bg-primary_tint flex items-center justify-center shrink-0">
                        <FaUserShield size={16} className="text-primary" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-primary">Security Health</h3>
                        <Skeleton className="h-3 w-20 mt-1.5" />
                      </div>
                    </div>
                    <Skeleton className="h-3 w-full max-w-xs mt-3" />
                    <Skeleton className="h-3 w-32 mt-1.5" />
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0 sm:[&:nth-last-child(2)]:border-0">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Skeleton className="w-3 h-3 shrink-0" rounded="rounded-full" />
                          <Skeleton className="h-3 w-full max-w-[120px]" />
                        </div>
                        <Skeleton className="h-3 w-10 ml-2 shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-surface rounded-xl border border-border p-4 text-center">
                <Skeleton className="h-6 w-10 mx-auto" />
                <Skeleton className="h-2.5 w-16 mx-auto mt-2.5" />
              </div>
            ))}
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Left Column */}
            <div className="lg:col-span-2 space-y-5">

              <SectionCard icon={FaUserShield} title="Account & Access">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-2.5 border-b border-border/60 last:border-0">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <Skeleton className="w-3.5 h-3.5 shrink-0" rounded="rounded" />
                        <Skeleton className="h-3 w-full max-w-[90px]" />
                      </div>
                      <Skeleton className="h-3 w-14 shrink-0" />
                    </div>
                  ))}
                </div>
              </SectionCard>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <SectionCard icon={FaKey} title="Password">
                  <div className="flex items-start gap-3">
                    <Skeleton className="w-[38px] h-[38px] shrink-0" rounded="rounded-xl" />
                    <div className="min-w-0 flex-1">
                      <Skeleton className="h-3.5 w-40" />
                      <Skeleton className="h-3 w-24 mt-2" />
                      <Skeleton className="h-5 w-28 mt-2.5" rounded="rounded-full" />
                    </div>
                  </div>
                </SectionCard>

                <SectionCard icon={FaShieldAlt} title="Two-Factor Auth">
                  <div className="flex items-start gap-3">
                    <Skeleton className="w-[38px] h-[38px] shrink-0" rounded="rounded-xl" />
                    <div className="min-w-0 flex-1">
                      <Skeleton className="h-3.5 w-24" />
                      <Skeleton className="h-3 w-full max-w-[180px] mt-2" />
                      <Skeleton className="h-5 w-16 mt-2.5" rounded="rounded-full" />
                    </div>
                  </div>
                </SectionCard>
              </div>

              <SectionCard icon={FaLaptop} title="Active Sessions">
                <div className="p-4 rounded-xl bg-primary_tint border border-primary/10 mb-3">
                  <div className="flex items-start gap-3">
                    <Skeleton className="w-[34px] h-[34px] shrink-0" rounded="rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <Skeleton className="h-3.5 w-32" />
                      <Skeleton className="h-3 w-24 mt-2" />
                      <Skeleton className="h-3 w-40 mt-1.5" />
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Skeleton className="w-3.5 h-3.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <Skeleton className="h-3 w-28" />
                          <Skeleton className="h-2.5 w-36 mt-1.5" />
                        </div>
                      </div>
                      <Skeleton className="h-3 w-12 shrink-0" />
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              <SectionCard icon={FaBell} title="Security Alerts">
                <div className="space-y-2.5">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="p-3 rounded-lg border-l-4 border-border bg-surface_alt">
                      <div className="flex items-start gap-2">
                        <Skeleton className="w-3 h-3 mt-0.5 shrink-0" rounded="rounded-full" />
                        <div className="min-w-0 flex-1">
                          <Skeleton className="h-3 w-32" />
                          <Skeleton className="h-2.5 w-full mt-1.5" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard icon={FaHistory} title="Recent Login Activity">
                <div className="space-y-1">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-start gap-2.5 p-2 rounded-lg">
                      <Skeleton className="w-[23px] h-[23px] shrink-0" rounded="rounded-full" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <Skeleton className="h-3 w-24" />
                          <Skeleton className="h-2.5 w-10 shrink-0" />
                        </div>
                        <Skeleton className="h-2.5 w-32 mt-1.5" />
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error || !data) {
    return (
      <div className="h-full overflow-y-auto bg-background">
        <div className="max-w-[100rem] mx-auto w-full flex flex-col items-center justify-center py-32 text-center">
          <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mb-4">
            <FaExclamationTriangle size={22} className="text-secondary" />
          </div>
          <h3 className="text-sm font-semibold text-text_primary">Couldn't load security data</h3>
          <p className="text-xs text-text_muted mt-1 mb-4">Please check your connection and try again.</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary_hover transition-colors flex items-center gap-2"
          >
            <FaRedo size={12} /> Retry
          </button>
        </div>
      </div>
    );
  }

  const { security_score, account, password, two_factor, sessions, login_history, login_stats, security_alerts } = data;
  const score = security_score?.score ?? 0;
  const tone = scoreTone(score);
  const others = sessions?.others || [];
  const history = showAllHistory ? login_history : (login_history || []).slice(0, 5);

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="max-w-[100rem] mx-auto w-full py-3">

        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3 fade-in">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-primary">Login &amp; Security</h1>
            <p className="text-sm text-text_secondary mt-1">
              Review your account activity, sessions and security health
            </p>
          </div>
          <button
            onClick={fetchData}
            className="px-3 py-2 rounded-lg text-xs font-medium text-text_secondary bg-surface border border-border hover:bg-surface_alt transition-colors flex items-center gap-2"
          >
            <FaSyncAlt size={11} /> Refresh
          </button>
        </div>

        {/* Security Score Hero */}
        <div className="mb-5 fade-in">
          <div className="bg-surface rounded-2xl shadow-sm border border-border p-5 md:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Score gauge */}
              <div className="flex items-center gap-5">
                <div className="relative w-24 h-24 shrink-0">
                  <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
                    <path
                      className="text-border"
                      stroke="currentColor"
                      strokeWidth="3.2"
                      fill="none"
                      d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
                    />
                    <path
                      stroke={tone.ring}
                      strokeWidth="3.2"
                      strokeLinecap="round"
                      fill="none"
                      d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
                      strokeDasharray="97.4"
                      strokeDashoffset={97.4 - (97.4 * score) / 100}
                      style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`text-2xl font-bold ${tone.text}`}>{score}</span>
                    <span className="text-[10px] text-text_muted">/ 100</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-primary_tint flex items-center justify-center">
                      <FaUserShield size={16} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-primary">Security Health</h3>
                      <span className={`text-xs font-semibold ${tone.text}`}>{security_score?.level || "—"}</span>
                    </div>
                  </div>
                  <p className="text-xs text-text_muted mt-2 max-w-xs">
                    Your score reflects email verification, password freshness and recent login activity.
                  </p>
                </div>
              </div>

              {/* Factors checklist */}
              <div className="lg:col-span-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  {(security_score?.factors || []).map((f) => (
                    <div key={f.key} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0 sm:[&:nth-last-child(2)]:border-0">
                      <div className="flex items-center gap-2 min-w-0">
                        {f.available === false ? (
                          <FaInfoCircle size={12} className="text-text_muted shrink-0" />
                        ) : f.met ? (
                          <FaCheckCircle size={12} className="text-success shrink-0" />
                        ) : (
                          <FaTimesCircle size={12} className="text-secondary shrink-0" />
                        )}
                        <span className={`text-xs truncate ${f.met ? "text-text_primary" : "text-text_secondary"}`}>
                          {f.label}
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-text_muted shrink-0 ml-2">
                        {f.available === false ? "Soon" : `${f.met ? "+" : ""}${f.points} pts`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5 fade-in">
          <StatCard label="Successful logins"  value={login_stats?.success_total      ?? 0} tone="text-success"  />
          <StatCard label="Failed logins"       value={login_stats?.failed_total       ?? 0} tone="text-secondary" />
          <StatCard label="Failed (7 days)"     value={login_stats?.failed_last_7_days ?? 0} tone="text-warning"  />
          <StatCard label="Devices used"        value={login_stats?.distinct_devices   ?? 0} />
          <StatCard label="Locations"           value={login_stats?.distinct_locations ?? 0} />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Left Column */}
          <div className="lg:col-span-2 space-y-5 fade-in">

            {/* Account & Access */}
            <SectionCard icon={FaUserShield} title="Account & Access">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <InfoRow icon={FaEnvelope} label="Email verified"   badge={<Pill ok={account?.is_email_verified} okText="Verified" badText="Unverified" />} />
                <InfoRow icon={FaLock}     label="Account status"   badge={<Pill ok={!account?.is_account_locked} okText="Unlocked" badText="Locked" />} />
                <InfoRow icon={FaClock}    label="Last login"       value={account?.last_login_date ? <LocalDateTime utcTime={account.last_login_date} /> : "Never"} />
                <InfoRow icon={FaCheckCircle} label="Member since"  value={account?.member_since ? <LocalDateTime utcTime={account.member_since} type="date" /> : "—"} />
                <InfoRow icon={FaHistory}  label="Total logins"     value={account?.login_count ?? 0} />
                <InfoRow icon={FaExclamationTriangle} label="Failed attempts" value={account?.failed_login_attempts ?? 0} />
              </div>
            </SectionCard>

            {/* Password + 2FA */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SectionCard icon={FaKey} title="Password">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-primary_tint shrink-0">
                    <FaLock size={16} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text_primary">
                      Last changed {password?.age_days != null ? `${password.age_days} day${password.age_days === 1 ? "" : "s"} ago` : "—"}
                    </p>
                    <p className="text-xs text-text_muted mt-0.5">
                      {password?.last_changed ? <LocalDateTime utcTime={password.last_changed} type="date" /> : "No record"}
                    </p>
                    <div className="mt-2">
                      {password?.is_stale ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-warning_bg text-warning border border-warning_border">
                          <FaExclamationTriangle size={9} /> Consider updating
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-success_bg text-success border border-success_border">
                          <FaCheckCircle size={9} /> Up to date
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard icon={FaShieldAlt} title="Two-Factor Auth">
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-xl shrink-0 ${two_factor?.enabled ? "bg-success_bg" : "bg-surface_alt"}`}>
                    <FaFingerprint size={16} className={two_factor?.enabled ? "text-success" : "text-text_muted"} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text_primary">
                      {two_factor?.enabled ? "Enabled" : "Not enabled"}
                    </p>
                    <p className="text-xs text-text_muted mt-0.5">
                      {two_factor?.supported
                        ? "Add an extra layer of protection to your account."
                        : "Two-factor authentication isn't available yet."}
                    </p>
                    <div className="mt-2">
                      {two_factor?.supported ? (
                        <Pill ok={two_factor?.enabled} okText="Active" badText="Inactive" />
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-info_bg text-info border border-info_border">
                          <FaInfoCircle size={9} /> Coming soon
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Active Sessions */}
            <SectionCard
              icon={FaLaptop}
              title={`Active Sessions (${sessions?.active_count ?? 0})`}
              action={
                others.length > 0 && (
                  <button
                    onClick={handleRevokeOthers}
                    disabled={revokingAll}
                    className="text-xs font-medium text-secondary hover:text-secondary_hover flex items-center gap-1 disabled:opacity-50"
                  >
                    <FaSignOutAlt size={11} /> {revokingAll ? "Signing out…" : "Sign out others"}
                  </button>
                )
              }
            >
              {/* Current session */}
              {sessions?.current ? (
                <div className="p-4 rounded-xl bg-primary_tint border border-primary/10 mb-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <FaDesktop size={18} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-medium text-text_primary truncate">
                          {sessions.current.device || "This device"}
                        </h4>
                        <span className="text-[11px] px-2 py-0.5 bg-success_bg text-success rounded-full flex items-center gap-1 shrink-0">
                          <span className="w-1.5 h-1.5 rounded-full bg-success" /> Current
                        </span>
                      </div>
                      <p className="text-xs text-text_muted mt-1">
                        {sessions.current.browser || "Unknown browser"}
                      </p>
                      <p className="text-xs text-text_muted mt-0.5 flex items-center gap-1">
                        <FaGlobe size={9} /> {sessions.current.location || "Unknown location"} · IP {sessions.current.ip || "—"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-background border border-dashed border-border text-center mb-3">
                  <p className="text-xs text-text_muted">No tracked current session.</p>
                </div>
              )}

              {/* Other sessions */}
              {others.length > 0 ? (
                <div className="space-y-1">
                  <h4 className="text-xs font-medium text-text_secondary mb-1">Other sessions</h4>
                  {others.map((s) => {
                    const DIcon = deviceIcon(s.device);
                    return (
                      <div key={s.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-surface_alt transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <DIcon size={14} className="text-text_muted shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-text_primary truncate">{s.device || "Unknown device"}</p>
                            <p className="text-[11px] text-text_muted truncate">
                              {s.location || "Unknown location"} · IP {s.ip || "—"}
                              {s.last_active && <> · <LocalDateTime utcTime={s.last_active} type="date" /></>}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevokeSession(s)}
                          disabled={busyId === s.id}
                          className="text-[11px] font-medium text-secondary hover:text-secondary_hover px-2 py-1 rounded hover:bg-secondary/5 transition-colors shrink-0 disabled:opacity-50"
                        >
                          {busyId === s.id ? "Revoking…" : "Revoke"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-text_muted text-center py-2">No other active sessions. 🎉</p>
              )}
            </SectionCard>
          </div>

          {/* Right Column */}
          <div className="space-y-5 fade-in">

            {/* Security Alerts */}
            <SectionCard icon={FaBell} title="Security Alerts">
              {security_alerts?.length ? (
                <div className="space-y-2.5">
                  {security_alerts.map((a, i) => {
                    const t = alertTone(a.severity);
                    const AIcon = t.icon;
                    return (
                      <div key={i} className={`p-3 rounded-lg border-l-4 ${t.wrap}`}>
                        <div className="flex items-start gap-2">
                          <AIcon size={12} className={`${t.iconColor} mt-0.5 shrink-0`} />
                          <div className="min-w-0">
                            <p className={`text-xs font-semibold ${t.title}`}>{a.title}</p>
                            <p className="text-[11px] text-text_secondary mt-0.5">{a.message}</p>
                            {a.date && (
                              <p className="text-[10px] text-text_muted mt-1">
                                <LocalDateTime utcTime={a.date} />
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FaCheckCircle size={20} className="text-success mx-auto mb-2" />
                  <p className="text-xs text-text_muted">No security alerts. You're all clear.</p>
                </div>
              )}
            </SectionCard>

            {/* Recent Login Activity */}
            <SectionCard icon={FaHistory} title="Recent Login Activity">
              {history?.length ? (
                <>
                  <div className="space-y-1">
                    {history.map((h) => {
                      const ok = h.status === "success";
                      return (
                        <div key={h.id} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-surface_alt transition-colors">
                          <div className={`p-1.5 rounded-full shrink-0 ${ok ? "bg-success_bg" : "bg-secondary/10"}`}>
                            {ok ? (
                              <FaCheckCircle size={10} className="text-success" />
                            ) : (
                              <FaExclamationTriangle size={10} className="text-secondary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-medium text-text_primary truncate">
                                {h.browser || h.device || "Unknown device"}
                              </p>
                              <span className="text-[10px] text-text_muted shrink-0">
                                <LocalDateTime utcTime={h.date} type="time" />
                              </span>
                            </div>
                            <p className="text-[11px] text-text_muted mt-0.5 truncate">
                              {h.location || "Unknown location"} · {h.auth_method || "—"}
                            </p>
                            <p className="text-[10px] text-text_muted">
                              <LocalDateTime utcTime={h.date} type="date" />
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {login_history.length > 5 && (
                    <button
                      onClick={() => setShowAllHistory((v) => !v)}
                      className="mt-3 text-xs font-medium text-primary hover:text-primary_hover flex items-center gap-1"
                    >
                      {showAllHistory ? "Show less" : `View all (${login_history.length})`}
                    </button>
                  )}
                </>
              ) : (
                <p className="text-xs text-text_muted text-center py-4">No login activity recorded.</p>
              )}
            </SectionCard>
          </div>
        </div>
      </div>

      {confirmData.isOpen && (
        <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
      )}
    </div>
  );
}
